import { h, Host } from "@stencil/core";
/**
 * @slot (default) - insert a standard, valid HTML video element
 */
export class VideoPlayer {
    constructor() {
        this.noCustomPlayButton = false;
        this.isInitialized = false;
        this.isPaused = true;
        this.isEnded = false;
        this.hasVideoControls = false;
        this.hasOwnPoster = false;
        this.hasLoopAutoplay = false;
        this.hasAutoplayNoControls = false;
        this.hasIntercepted = false;
    }
    /**
     * Primary element reference
     */
    element;
    /**
     * Event emitted when video controls are changed
     * @event uxVideoControlChange
     */
    videoControlChange;
    baseClass = "ux-video";
    videoElement;
    videoWrapper;
    haveControlsBeenSet = false;
    isBrowserSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    autoplayAttr = "data-autoplay";
    /**
     * Safari does not load the first frame of the video as poster with preload="metadata"
     */
    preloadAttribute = this.isBrowserSafari ? "auto" : "metadata";
    handleIntersection = (entries) => {
        entries.map((entry) => {
            if (entry.isIntersecting) {
                this.onVideoIntersecting();
                if (this.videoElement.hasAttribute(this.autoplayAttr)) {
                    if (this.videoElement.hasAttribute("loop") || !this.hasIntercepted) {
                        this.videoElement.muted = true;
                        this.videoElement.play();
                    }
                    this.hasIntercepted = true;
                }
            }
            else {
                if (!this.isPaused && this.videoElement.hasAttribute("loop")) {
                    this.videoElement.pause();
                    this.handlePause();
                }
            }
        });
    };
    intersectionObserver = new IntersectionObserver(this.handleIntersection);
    onVideoIntersecting = () => {
        if (this.videoElement.getAttribute("preload") === "none") {
            if (this.videoElement.videoWidth === undefined) {
                // Only set minHeight if metadata is not yet loaded.
                // The "loadedmetadata" event listener is attached in the connectedCallback.
                this.videoWrapper.style.minHeight = `${this.videoWrapper.clientHeight}px`;
            }
            this.videoElement.setAttribute("preload", this.preloadAttribute);
            if (this.isBrowserSafari) {
                if (!this.hasOwnPoster) {
                    const source = this.videoElement.querySelector("source");
                    // @ts-expect-error Check for typing
                    const srcAttribute = source.getAttribute("src");
                    // @ts-expect-error Check for typing
                    source.setAttribute("src", srcAttribute + "#t=0.1");
                    this.videoElement.load();
                }
            }
        }
        if (!this.hasOwnPoster && this.videoElement.hasAttribute("poster")) {
            this.videoElement.removeAttribute("poster");
        }
    };
    /** Mutation observer */
    contentObserver = new MutationObserver(() => this.onWrapperClassChange());
    onWrapperClassChange = () => {
        if (this.videoWrapper.classList.contains("is-videoPlaying")) {
            // @ts-expect-error Check for typing
            this.element.parentElement.classList.add("is-videoPlaying");
            if (!this.haveControlsBeenSet && this.hasVideoControls) {
                this.videoElement.setAttribute("controls", "");
                this.haveControlsBeenSet = true;
            }
        }
        else {
            // @ts-expect-error Check for typing
            this.element.parentElement.classList.remove("is-videoPlaying");
        }
    };
    handleMetadata = () => {
        this.videoWrapper.removeAttribute("style");
    };
    handlePlay = () => {
        this.videoControlChange.emit({
            type: "play",
            element: this.videoElement
        });
        this.isPaused = false;
        if (this.isEnded) {
            setTimeout(() => (this.isEnded = false), 500);
        }
        if (this.haveControlsBeenSet) {
            return;
        }
        if (this.hasVideoControls) {
            this.videoElement.setAttribute("controls", "");
            this.haveControlsBeenSet = true;
        }
    };
    handlePause = () => {
        this.videoControlChange.emit({
            type: "pause",
            element: this.videoElement
        });
        this.isPaused = true;
    };
    handleEnd = () => {
        this.videoControlChange.emit({
            type: "ended",
            element: this.videoElement
        });
        this.isEnded = true;
    };
    handleVolumeChange = () => {
        this.videoControlChange.emit({
            type: "volumechange",
            element: this.videoElement
        });
    };
    connectedCallback() {
        this.videoElement = this.element.querySelector("video");
        if (this.videoElement === null) {
            return;
        }
        if (this.videoElement.hasAttribute("autoplay")) {
            this.videoElement.removeAttribute("autoplay");
            this.videoElement.setAttribute(this.autoplayAttr, "");
        }
        this.videoElement.addEventListener("loadedmetadata", this.handleMetadata);
        this.videoElement.addEventListener("play", this.handlePlay);
        this.videoElement.addEventListener("pause", this.handlePause);
        this.videoElement.addEventListener("ended", this.handleEnd);
        this.videoElement.addEventListener("volumechange", this.handleVolumeChange);
        if (this.isInitialized) {
            return;
        }
        this.hasOwnPoster = !!this.videoElement.getAttribute("poster");
        if (this.videoElement.getAttribute("controls") !== null && this.videoElement.hasAttribute("controls")) {
            this.hasVideoControls = true;
            this.videoElement.removeAttribute("controls");
        }
        else if (!this.videoElement.hasAttribute("controls") && !this.videoElement.hasAttribute(this.autoplayAttr)) {
            this.hasVideoControls = true;
        }
        this.isInitialized = true;
    }
    componentDidLoad() {
        this.contentObserver.observe(this.videoWrapper, {
            attributes: true
        });
        if (this.videoElement.hasAttribute(this.autoplayAttr)) {
            if (this.hasVideoControls && !this.haveControlsBeenSet) {
                this.videoElement.setAttribute("controls", "");
                this.haveControlsBeenSet = true;
            }
            else if (!this.hasVideoControls && this.videoElement.hasAttribute("loop")) {
                this.hasLoopAutoplay = true;
                if (this.videoElement.hasAttribute("muted")) {
                    this.videoElement.muted = true;
                }
            }
            else if (!this.hasVideoControls) {
                this.hasAutoplayNoControls = true;
            }
        }
        this.isPaused = this.videoElement.paused;
        this.intersectionObserver.observe(this.element);
    }
    disconnectedCallback() {
        if (this.videoElement === null) {
            return;
        }
        this.videoElement.removeEventListener("loadedmetadata", this.handleMetadata);
        this.videoElement.removeEventListener("play", this.handlePlay);
        this.videoElement.removeEventListener("pause", this.handlePause);
        this.videoElement.removeEventListener("ended", this.handleEnd);
        this.videoElement.removeEventListener("volumechange", this.handleVolumeChange);
    }
    render() {
        const { baseClass, isPaused, hasLoopAutoplay, hasAutoplayNoControls, noCustomPlayButton, haveControlsBeenSet } = this;
        return (h(Host, { key: '25c0f9a1b07ffdbc700fb5fcde240c6db7ef1c7e', class: baseClass }, h("div", { key: 'c7cf97808a4d4f44939254d6d089e7cbd63bb81d', class: `${baseClass}__video-wrapper`, ref: (el) => (this.videoWrapper = el) }, h("slot", { key: 'e318bf890fbd552f16ddc7cf0765989f673e1cf5' }), !noCustomPlayButton && (h("button", { key: 'fedb294bde30bc35fe12e0e250b850e36de0e59d', class: {
                [`${baseClass}__play-button`]: true,
                [`${baseClass}__play-button--inactive`]: !isPaused || hasLoopAutoplay || hasAutoplayNoControls,
                [`${baseClass}__play-button--controls-active`]: haveControlsBeenSet
            }, type: "button", onClick: () => this.videoElement.play(), "aria-hidden": isPaused ? "false" : "true", "aria-label": "play", name: "play" }, h("ux-icon", { key: '4371c837bb5f3969448a25a8c3efe2c58826e779', class: `${baseClass}__play-icon`, name: "play", weight: "bold", filled: true }))))));
    }
    static get is() { return "ux-video"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["video.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["video.css"]
        };
    }
    static get properties() {
        return {
            "noCustomPlayButton": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "false",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "false"
                        }],
                    "text": "Control usage of custom play button\nWill be set automatically if on of the parents has the class,b `js-video-play-button-handler`\nmeaning that the video has a custom overlay"
                },
                "attribute": "no-custom-play-button",
                "reflect": true,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "isInitialized": {},
            "isPaused": {},
            "isEnded": {},
            "hasVideoControls": {},
            "hasOwnPoster": {},
            "hasLoopAutoplay": {},
            "hasAutoplayNoControls": {},
            "hasIntercepted": {}
        };
    }
    static get events() {
        return [{
                "method": "videoControlChange",
                "name": "uxVideoControlChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": "uxVideoControlChange"
                        }],
                    "text": "Event emitted when video controls are changed"
                },
                "complexType": {
                    "original": "{\n        type: \"play\" | \"pause\" | \"ended\" | \"volumechange\";\n        element: HTMLVideoElement;\n    }",
                    "resolved": "{ type: \"ended\" | \"pause\" | \"play\" | \"volumechange\"; element: HTMLVideoElement; }",
                    "references": {
                        "HTMLVideoElement": {
                            "location": "global",
                            "id": "global::HTMLVideoElement"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "element"; }
}
//# sourceMappingURL=video.js.map
